/**
 * match.v1 cizici — ÇİZGİYLE BAĞLAMA. Solda tanim kutulari (sag kenarda nokta),
 * sagda KARISIK kelimeler (sol kenarda nokta). Cocuk once bir tanima (nokta
 * aktif olur), sonra dogru kelimeye dokunur; aralarina renkli cizgi cizilir.
 * Bagli bir ogeye tekrar dokunmak baglantiyi bozar.
 *
 * Icerik (client): { prompt?, left:[{id,text}], right:[{id,text}] }
 *   (dogru eslesme `match` yalnizca authoring'de; client'a gelmez.)
 * Cevap: onAnswerChange({ pairs:{ <leftId>:<rightId> } }); tum sol ogeler
 *   baglanana kadar null. Skorlama SUNUCUDA (left.id -> right.match).
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { MechanicProps } from "./types";

interface MatchSide {
  id: string;
  text: string;
}
interface MatchContent {
  prompt?: { text?: string; subText?: string };
  left?: MatchSide[];
  right?: MatchSide[];
}

// mor tonları: 1 (koyu) -> 10 (açık) kademeli
// Tek, temiz ECUP moru — çizgiler + rozetler + seçili köşelikler. Çiftler numara rozetiyle ayrışır.
const ECUP_MOR = "#820AD1";

export function MatchItem({ item, onAnswerChange }: MechanicProps) {
  const c = item.content as unknown as MatchContent;
  const left = c.left ?? [];
  const right = c.right ?? [];

  const saved = item.answer?.pairs as Record<string, string> | undefined;
  const [pairs, setPairs] = useState<Record<string, string>>(saved ?? {});
  const [activeLeft, setActiveLeft] = useState<string | null>(null);

  const wrapRef = useRef<HTMLDivElement>(null);
  const leftDot = useRef<Record<string, HTMLSpanElement | null>>({});
  const rightDot = useRef<Record<string, HTMLSpanElement | null>>({});
  const [tick, setTick] = useState(0); // layout sonrası çizgileri yeniden hesapla

  const cb = useRef(onAnswerChange);
  cb.current = onAnswerChange;
  useEffect(() => {
    const done = left.length > 0 && left.every((l) => pairs[l.id]);
    cb.current(done ? { pairs } : null, Object.keys(pairs).length > 0 ? { pairs } : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pairs]);

  // ilk mount + yeniden boyutlandırmada çizgi koordinatlarını tazele
  useLayoutEffect(() => {
    setTick((n) => n + 1);
    const on = () => setTick((n) => n + 1);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  const colorOf = (_leftId: string) => ECUP_MOR;
  const numOf = (leftId: string) => left.findIndex((l) => l.id === leftId) + 1;
  const rightToLeft: Record<string, string> = {};
  Object.keys(pairs).forEach((l) => {
    const r = pairs[l];
    if (r) rightToLeft[r] = l;
  });

  function clickLeft(id: string) {
    if (pairs[id]) {
      setPairs((p) => {
        const n = { ...p };
        delete n[id];
        return n;
      });
      setActiveLeft(id);
      return;
    }
    setActiveLeft((a) => (a === id ? null : id));
  }
  function clickRight(id: string) {
    const owner = rightToLeft[id];
    if (owner && !activeLeft) {
      setPairs((p) => {
        const n = { ...p };
        delete n[owner];
        return n;
      });
      return;
    }
    if (!activeLeft) return;
    setPairs((p) => {
      const n = { ...p };
      const prevOwner = Object.keys(n).find((k) => n[k] === id);
      if (prevOwner) delete n[prevOwner];
      n[activeLeft] = id;
      return n;
    });
    setActiveLeft(null);
  }

  // çizgi koordinatları (nokta merkezleri, container'a göre)
  const lines: Array<{ x1: number; y1: number; x2: number; y2: number; color: string }> = [];
  const wrap = wrapRef.current;
  if (wrap) {
    const wr = wrap.getBoundingClientRect();
    // kısa ekranda .mech zoom ile küçülür: ekran px'i SVG'de bir kez daha küçülüp çizgiler sola/yukarı kayıyordu.
    // Ekran ölçüsü / yerleşim ölçüsü = etkin zoom; koordinatlar buna bölünür.
    const z = wrap.offsetWidth > 0 ? wr.width / wrap.offsetWidth : 1;
    Object.keys(pairs).forEach((lid) => {
      const rid = pairs[lid];
      const ld = leftDot.current[lid];
      const rd = rid ? rightDot.current[rid] : null;
      if (!ld || !rd) return;
      const a = ld.getBoundingClientRect();
      const b = rd.getBoundingClientRect();
      lines.push({
        x1: (a.left + a.width / 2 - wr.left) / z,
        y1: (a.top + a.height / 2 - wr.top) / z,
        x2: (b.left + b.width / 2 - wr.left) / z,
        y2: (b.top + b.height / 2 - wr.top) / z,
        color: colorOf(lid),
      });
    });
  }
  void tick; // tick değişince yeniden render → koordinatlar güncellenir

  return (
    <div className="mech match-mech">
      {c.prompt?.text && <h2 className="prompt">{c.prompt.text}</h2>}
      {c.prompt?.subText && <p className="prompt-sub">{c.prompt.subText}</p>}

      <div className="match-wrap" ref={wrapRef}>
        <svg className="match-lines" aria-hidden="true">
          {lines.map((ln, i) => (
            <line
              key={i}
              x1={ln.x1}
              y1={ln.y1}
              x2={ln.x2}
              y2={ln.y2}
              stroke={ln.color}
              strokeWidth={4}
              strokeLinecap="round"
            />
          ))}
        </svg>

        <div className="match-cols">
          <div className="match-col match-left">
            {left.map((l, i) => {
              const paired = !!pairs[l.id];
              const active = activeLeft === l.id;
              const col = colorOf(l.id);
              return (
                <button
                  type="button"
                  key={l.id}
                  className={`match-item${active ? " active" : ""}${paired ? " paired" : ""}`}
                  style={active || paired ? { borderColor: col } : undefined}
                  onClick={() => clickLeft(l.id)}
                  aria-pressed={active}
                >
                  <span className="match-num" style={{ background: col }}>{i + 1}</span>
                  <span className="match-text">{l.text}</span>
                  <span
                    className="match-dot"
                    ref={(el) => { leftDot.current[l.id] = el; }}
                    style={{ background: active || paired ? col : "#c7ccd4" }}
                  />
                </button>
              );
            })}
          </div>

          <div className="match-col match-right">
            {right.map((r) => {
              const owner = rightToLeft[r.id];
              const col = owner ? colorOf(owner) : undefined;
              return (
                <button
                  type="button"
                  key={r.id}
                  className={`match-item word${owner ? " paired" : ""}`}
                  style={owner ? { borderColor: col } : undefined}
                  onClick={() => clickRight(r.id)}
                >
                  <span
                    className="match-dot"
                    ref={(el) => { rightDot.current[r.id] = el; }}
                    style={{ background: owner ? col : "#c7ccd4" }}
                  />
                  <span className="match-text">{r.text}</span>
                  {owner && <span className="match-num sm" style={{ background: col }}>{numOf(owner)}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
